import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Live countdown to the next daily-DD baseline reset (00:00 UTC = 03:00 EAT).
 * Sits under the Daily Drawdown progress bar. Ticks every 30s (no per-second
 * animation churn — the granularity users care about is hours/minutes).
 */
function msUntilNextUtcMidnight() {
    const now = new Date();
    const next = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0, 0,
    ));
    return Math.max(0, next.getTime() - now.getTime());
}

function fmtRemaining(ms) {
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h >= 1) return `${h}h ${String(m).padStart(2, "0")}m`;
    return `${m}m`;
}

export default function DailyResetChip() {
    const [remaining, setRemaining] = useState(() => msUntilNextUtcMidnight());

    useEffect(() => {
        const tick = () => setRemaining(msUntilNextUtcMidnight());
        // Refresh every 30s — plenty for hour/minute display.
        const id = setInterval(tick, 30_000);
        // Also refresh when the tab regains focus (users leaving overnight).
        const onVisible = () => { if (!document.hidden) tick(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
    }, []);

    const label = useMemo(() => fmtRemaining(remaining), [remaining]);

    return (
        <div
            className="mt-2 inline-flex items-center gap-1.5 text-[10px] text-muted-foreground/80"
            title="Your daily drawdown budget resets at 00:00 UTC (03:00 EAT) every trading day."
            data-testid="daily-reset-chip"
        >
            <Clock className="w-3 h-3 shrink-0" />
            <span className="uppercase tracking-wider">Daily reset</span>
            <span className="mono proportional-nums text-foreground/80" data-testid="daily-reset-remaining">· in {label}</span>
        </div>
    );
}
